
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const urlEncodedData = new URLSearchParams();
      formDataToSend.forEach((value, key) => {
        urlEncodedData.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d5vsnjms8bb84h5if0o0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setTimeout(() => {
          const successMessage = document.getElementById('contact-success-message');
          if (successMessage) {
            successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        console.error('Form submission failed:', response.status, response.statusText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'subject' && value === 'Artist Submission') {
      window.REACT_APP_NAVIGATE('/artists');
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-normal text-gray-900 mb-6 uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
            {t('contact.title')}
          </h2>
          <div className="w-24 h-1 bg-amber-700 mx-auto mb-8"></div>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
          {/* Contact Info */}
          <div className="space-y-6 sm:space-y-8">
            <div>
              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mb-4 sm:mb-6 uppercase font-display">{t('contact.letsConnect')}</h3>
              <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                {t('contact.connectDescription')}
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-amber-50 rounded-lg p-4 sm:p-6 flex items-start gap-3 sm:gap-4 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-700 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-map-pin-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1 text-sm sm:text-base">{t('contact.location')}</h4>
                  <p className="text-gray-600 text-xs sm:text-sm">{t('contact.locationValue')}</p>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg p-4 sm:p-6 flex items-start gap-3 sm:gap-4 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-700 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-mail-line text-white text-lg sm:text-xl"></i>
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-gray-900 mb-1 text-sm sm:text-base">{t('contact.email')}</h4>
                  <a href="mailto:info@ranchfireradio.com" className="text-amber-700 hover:text-amber-800 text-xs sm:text-sm break-all cursor-pointer">
                    info@ranchfireradio.com
                  </a>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 uppercase tracking-wide text-xs sm:text-sm">{t('contact.followUs')}</h4>
              <div className="flex gap-2 sm:gap-3">
                <a href="https://www.facebook.com/profile.php?id=61580107936003" target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 hover:bg-amber-700 rounded-full flex items-center justify-center text-amber-800 hover:text-white transition-all duration-300 hover:scale-110 cursor-pointer">
                  <i className="ri-facebook-fill text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 hover:bg-amber-700 rounded-full flex items-center justify-center text-amber-800 hover:text-white transition-all duration-300 hover:scale-110 cursor-pointer">
                  <i className="ri-twitter-x-line text-lg sm:text-xl"></i>
                </a>
                <a href="https://www.instagram.com/ranchfire_radio/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 hover:bg-amber-700 rounded-full flex items-center justify-center text-amber-800 hover:text-white transition-all duration-300 hover:scale-110 cursor-pointer">
                  <i className="ri-instagram-line text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 hover:bg-amber-700 rounded-full flex items-center justify-center text-amber-800 hover:text-white transition-all duration-300 hover:scale-110 cursor-pointer">
                  <i className="ri-spotify-fill text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 hover:bg-amber-700 rounded-full flex items-center justify-center text-amber-800 hover:text-white transition-all duration-300 hover:scale-110 cursor-pointer">
                  <i className="ri-youtube-fill text-lg sm:text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-amber-50 rounded-2xl p-6 sm:p-8 shadow-lg">
            {/* Success Message */}
            {submitStatus === 'success' && (
              <div id="contact-success-message" className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-xl"></i>
                  <span className="font-bold">{t('contact.successTitle')}</span>
                </div>
                <p className="text-sm mt-1">{t('contact.successMessage')}</p>
              </div>
            )}

            {/* Error Message */}
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-fill text-xl"></i>
                  <span className="font-bold">{t('contact.errorTitle')}</span>
                </div>
                <p className="text-sm mt-1">{t('contact.errorMessage')}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-4 sm:space-y-6">
              <div>
                <label htmlFor="name" className="block text-xs sm:text-sm font-bold text-gray-900 mb-2 uppercase tracking-wide">
                  {t('contact.yourName')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white border border-amber-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                  placeholder={t('contact.namePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-bold text-gray-900 mb-2 uppercase tracking-wide">
                  {t('contact.emailAddress')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white border border-amber-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-xs sm:text-sm font-bold text-gray-900 mb-2 uppercase tracking-wide">
                  {t('contact.subject')}
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white border border-amber-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">{t('contact.selectSubject')}</option>
                  <option value="Artist Submission">{t('contact.artistSubmission')}</option>
                  <option value="Sponsorship Inquiry">{t('contact.sponsorshipInquiry')}</option>
                  <option value="General Feedback">{t('contact.generalFeedback')}</option>
                  <option value="Technical Support">{t('contact.technicalSupport')}</option>
                  <option value="Other">{t('contact.other')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs sm:text-sm font-bold text-gray-900 mb-2 uppercase tracking-wide">
                  {t('contact.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-amber-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-700 focus:border-transparent resize-none text-sm"
                  placeholder={t('contact.messagePlaceholder')}
                ></textarea>
                <p className="text-xs text-gray-600 mt-1">{formData.message.length}/500 {t('contact.characters')}</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-amber-700/30 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isSubmitting ? t('contact.sending') : t('contact.sendMessage')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
